// useProcessKeyboard — keyboard accelerators for the process editor (plan §5
// P4). Acts on the ACTIVE step: Delete/Backspace removes it, Escape deselects.
// Guards: only in edit mode, never while typing in an input/textarea/editable
// (so it can't hijack the inline-edit fields or the ⌘K palette). Surface-
// agnostic — the host injects the active step's nodeId + the funnel callbacks —
// so it is unit-tested with synthetic key events and works for every process
// surface at once. Text stays truth: Delete routes through deleteStep → the
// mutation funnel (undo covers it), never an optimistic removal.

import { useEffect, useRef } from "react";

export interface ProcessKeyboardOptions {
  /** Off outside edit mode (read-only view must not mutate on a keypress). */
  readonly enabled: boolean;
  /** The active/selected step's nodeId, or null when nothing is selected. */
  readonly activeNodeId: string | null;
  /** Delete the active step (→ deleteStep → funnel). */
  readonly onDelete: (nodeId: string) => void;
  /** Clear the selection. */
  readonly onDeselect: () => void;
}

/** True when the event originates from a text-entry surface we must not hijack —
 *  native fields, contenteditable, plus code editors (CM6 / Monaco) whose
 *  focused element isn't itself a textarea. */
function isTypingTarget(t: EventTarget | null): boolean {
  const el = t as HTMLElement | null;
  if (!el || !el.tagName) return false;
  if (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable === true) return true;
  return typeof el.closest === "function" && el.closest(".cm-editor, .monaco-editor") !== null;
}

export function useProcessKeyboard(opts: ProcessKeyboardOptions): void {
  // Read the latest opts inside the listener without re-subscribing each render.
  const ref = useRef(opts);
  ref.current = opts;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const o = ref.current;
      if (!o.enabled || isTypingTarget(e.target)) return;
      if ((e.key === "Delete" || e.key === "Backspace") && o.activeNodeId) {
        e.preventDefault();
        o.onDelete(o.activeNodeId);
      } else if (e.key === "Escape" && o.activeNodeId) {
        o.onDeselect();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
}
